import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { FeatureFlagsContext } from "contexts/FeatureFlagsContext";
import usePermissions from "hooks/usePermission";
import { useEndVisitTiming } from "hooks/useEndVisitTiming";
import { usePifTiming } from "hooks/usePifTiming";
import { useScreenTimeTracking } from "hooks/useScreenTimeTracking";
import { Suspense, useContext } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { annotationInterfaceFeature, isFeatureEnabled } from "utils/featureFlags";
import PrivateRoute from "../Private";

// Lazy loaded routes
import {
  AnnotationInterface,
  AppointmentsDashboard,
  CreateNewPassword,
  EndVisitStepper,
  ForgotPassword,
  Login,
  NotFound,
  OpenEncountersDashboard,
  PatientDashboard,
  PatientDetail,
  SettingsWrapper,
} from "../Lazy";

const LoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
    <CircularProgress />
  </Box>
);

const Router = () => {
  useScreenTimeTracking();
  useEndVisitTiming();
  usePifTiming();
  const { hasPermission } = usePermissions();
  const flagsData = useContext(FeatureFlagsContext);
  const annotationEnabled = isFeatureEnabled(flagsData, annotationInterfaceFeature);

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/createNewPassword/:hiddenToken" element={<CreateNewPassword />} />
        <Route path="/forgotPassword" element={<ForgotPassword />} />
        <Route path="/createNewPassword/:hiddenToken/:email" element={<CreateNewPassword />} />
        <Route element={<PrivateRoute />}>
          {/* redirect / to patients */}
          {hasPermission("view_appointment") && <Route path="/" element={<Navigate to="/appointments" />} />}
          {hasPermission("view_appointment") && <Route path="/appointments" element={<AppointmentsDashboard />} />}
          {hasPermission("view_patient") && <Route path="/patients" element={<PatientDashboard />} />}
          {hasPermission("view_encounter") && <Route path="/encounters" element={<OpenEncountersDashboard />} />}
          {hasPermission("view_patient") && (
            <Route path="/schedule/patient/:patientId/appointment/:appointmentId" element={<PatientDetail />} />
          )}
          {hasPermission("view_patient") && <Route path="/patients/:patientId" element={<PatientDetail />} />}

          {hasPermission("view_patient") && (
            <Route path="/patients/:patientId/interimScreen" element={<EndVisitStepper {...({} as any)} />} />
          )}
          <Route path="/settings" element={<SettingsWrapper />} />
          <Route path="/settings?tab=:tab" element={<SettingsWrapper />} />
          {annotationEnabled ? (
            <Route path="/annotations" element={<AnnotationInterface />} />
          ) : (
            <Route path="/annotations" element={<Navigate to="/" />} />
          )}
          <Route path="*" element={<NotFound />} />
        </Route>
        {/* Catch-all route for 404 - Page Not Found */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default Router;
